"use client";
import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import Chat from ".";
import ShortBar from "./ShortBar";
import {
  ScrollAction,
  ScrollFrom,
  useScrollToBottom,
} from "@/app/hooks/useReadScrollToBottom";
import ChatInput from "../ChatInput";
import AssociateButtonList from "../robot/components/AssociateButtonList";
import {
  reporterInstance,
  ReportScene,
  reportScene,
} from "@/app/services/DataReporter";
import ChatHeader from "../chatHeader";
import AgentNotService from "./AgentNotService";
import useVConsole from "@/app/hooks/useVConsole";
import { ConversationStatus, hasActiveConversation } from "@/app/api/message";
import { webSocketService } from "@/app/services/WebSocketService";
import { CurrentChatStatus } from "@/app/store/types";
import useLongPolling from "@/app/hooks/useLongPolling";
import { authService } from "@/app/services/AuthService";
import ErrorPage from "../errorPage";
import { useMessageStore } from "@/app/store";
import Locale from "@/app/locales";

import { isDisConnectedNeedReconnect } from "@/app/store/formatMessage";
import { useAuth } from "@/app/lib/AuthProvider";
import { isMobileDevice } from "@/app/utils/device";
import { useClientInit } from "@/app/hooks/useClientInit";
import { useURLSearchParams } from "@/app/hooks/useURLSearchParams";
import { sleep } from "@/app/utils/async";

function ChatBody() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageVisible, setPageVisible] = useState<boolean | undefined>(
    undefined,
  );
  const [online, setOnline] = useState(true); // 默认为 true，游客可以看到界面
  const [disOnlineContent, setDisOnlineContent] = useState("");
  const { scrollToBottom } = useScrollToBottom(containerRef);
  const {
    resetUnreadCount,
    getUnreadMessages,
    setBottomReachedStatus,
    setSessionInfo,
    setCurrentChatStatus,
  } = useMessageStore();

  // 获取认证状态和路由参数
  const {
    isAuthenticated,
    isLoading: authLoading,
    setAuthenticated,
  } = useAuth();
  const searchParams = useURLSearchParams();

  const handleScrollToBottom = (action?: ScrollAction, from?: ScrollFrom) =>
    scrollToBottom(containerRef, { actionType: action, pageVisible, from });

  // 初始化客服会话和历史消息
  // 职责：建立 WebSocket 连接、加载历史记录、获取问题类型等
  // 注意：只有在认证检查完成后才执行初始化
  const shouldInitialize = !authLoading;

  useClientInit(
    async () => {
      // 初始化成功后，滚动到底部
      await sleep(200);
      handleScrollToBottom(
        ScrollAction.FORCE_TO_BOTTOM,
        ScrollFrom.INIT_SUCCESS,
      );
    },
    searchParams ?? undefined,
    isAuthenticated,
    setAuthenticated,
    shouldInitialize, // 新增参数：是否应该初始化
  );

  // Token 续期：只在已登录时执行
  useLongPolling(() => {
    if (isAuthenticated) {
      authService.renewalToken();
    }
  }, 10000);

  useVConsole();

  const pageVisibleChange = () => {
    const pageIsHidden = document.hidden;
    setPageVisible(!pageIsHidden);

    // 只在已登录时执行
    if (!isAuthenticated) {
      return;
    }

    if (!pageIsHidden) {
      reportScene(ReportScene.PAGE_SHOW);
      // 当页面展示时，如果有未读数，是不允许滚动到底部的
      if (getUnreadMessages().length > 0) {
        setBottomReachedStatus(false);
      }
      // 当页面展示时，立即尝试判断socket
      hasActiveConversation()
        .then((sessionInfo) => {
          if (
            sessionInfo.hasActiveConversation &&
            isDisConnectedNeedReconnect()
          ) {
            // 如果在会话中，尝试发起重连
            webSocketService.handleReconnectOffline();
          }
        })
        .catch((error) => {
          console.warn("Check conversation failed:", error);
        });
    } else {
      reportScene(ReportScene.PAGE_HIDE);
      // 隐藏页面立即上报
      reporterInstance.flush();
    }
  };

  const onlineChange = async () => {
    // 只在已登录时执行
    if (!isAuthenticated) {
      console.log("游客模式：跳过会话状态检查");
      return;
    }

    try {
      // 可以在这里执行数据同步等操作
      const sessionInfo = await hasActiveConversation();
      const isConnected = webSocketService.isConnected();
      setSessionInfo(sessionInfo);
      console.log(
        "网络恢复状态：",
        isConnected,
        sessionInfo.hasActiveConversation,
      );
      // 未链接并且sessionInfo.hasActiveConversation存在，则需要重新发起socket链接
      if (!isConnected && sessionInfo.hasActiveConversation) {
        // 发起重连
        webSocketService.handleReconnectOffline();
        if (sessionInfo.status === ConversationStatus.LineUp) {
          // 重连成功后，状态为排队中
          useMessageStore
            .getState()
            .setCurrentChatStatus(CurrentChatStatus.LineUp);
        } else {
          // 重连成功后，状态为在线
          useMessageStore
            .getState()
            .setCurrentChatStatus(CurrentChatStatus.Online);
        }
      }
    } catch (error) {
      console.error("Network change check failed:", error);
    }
  };

  useEffect(() => {
    // 只在客户端执行
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preventZoom = (event: any) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (event: Event) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    document.addEventListener("touchstart", preventZoom);
    document.addEventListener("touchend", preventDoubleTapZoom, {
      passive: false,
    });
    document.addEventListener("gesturestart", preventGesture);
    document.addEventListener("gesturechange", preventGesture);
    document.addEventListener("gestureend", preventGesture);
    document.documentElement.addEventListener(
      "dblclick",
      (e) => {
        e.preventDefault();
      },
      { passive: false },
    );

    return () => {
      document.removeEventListener("touchstart", preventZoom);
      document.removeEventListener("touchend", preventDoubleTapZoom);
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.documentElement.removeEventListener("dblclick", (e) => {
        e.preventDefault();
      });
    };
  }, []);

  useEffect(() => {
    // 只在已登录时执行初始化
    if (!isAuthenticated) {
      console.log("游客模式：跳过初始化");
      return;
    }

    // 重置未读数
    resetUnreadCount();
    document.title = Locale.systemMsg.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // 依赖认证状态，登录后重新初始化

  if (!online) {
    return <AgentNotService content={disOnlineContent} />;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorPage errorMsg="" canRetry />}
      onError={(error, componentStack, eventId) => {
        reportScene(
          ReportScene.RENDER_ERROR,
          JSON.stringify({
            error: (error as Error)?.message,
            stack: (error as Error)?.stack,
            componentStack,
            eventId,
            tag: "ChatBody",
          }),
        );
        reporterInstance.flush();
      }}
    >
      {/* <PrivacyPolicy /> */}
      <ChatHeader />
      {/* 聊天消息区域 */}
      <Chat
        handleScrollToBottom={handleScrollToBottom}
        containerRef={containerRef}
      />
      <ShortBar
        handleScrollToBottom={handleScrollToBottom}
        pageVisible={pageVisible}
      />
      {!isMobileDevice() && <AssociateButtonList type="pc" />}
      {/* 输入区 */}
      <ChatInput
        handleScrollToBottom={handleScrollToBottom}
        pageVisible={pageVisible}
      />
    </Sentry.ErrorBoundary>
  );
}

// 移除 withAuth，支持游客模式
export default ChatBody;
